import React, {
  forwardRef,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useImperativeHandle,
} from "react";
import { useFrame, extend } from "@react-three/fiber";
import * as THREE from "three";
import { shaderMaterial } from "@react-three/drei";

// Define a custom shader material for fire particles (vertex + fragment shaders)
const FireMaterial = shaderMaterial(
  {
    color: new THREE.Color(1.0, 0.4, 0.1), // Base flame color
    dpr: window.devicePixelRatio,         // Device pixel ratio for point sizing
    uOpacity: 0,                          // Uniform to control overall opacity
  },
  // Vertex Shader: size and aging logic for each point
  // age (0-1) 1 determines the death of the particle
  `
    uniform float dpr;
    attribute float age;
    attribute float size;
    varying float vAge;

    void main() {
      vAge = age;
      float scale = 4.0;
      gl_PointSize = size * scale * (1.0 - age) * dpr;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader: radial falloff + color interpolation based on age
  `
    varying float vAge;
    uniform vec3 color;
    uniform float uOpacity;

    void main() {
      float dist = length(gl_PointCoord - vec2(0.5));
      if (dist > 0.5) discard;

      vec3 flameColor = mix(vec3(1.0, 1.0, 0.0), color, vAge);
      float alpha = (1.0 - vAge) * (1.0 - dist * 2.0);
      gl_FragColor = vec4(flameColor, alpha * uOpacity);
    }
  `
);

// Make the custom material available in JSX as <fireMaterial />
extend({ FireMaterial });


const FireParticles = forwardRef(function FireParticles(
  {
    count = 300,
    position = [0, 0, 0],
    visible = true,
    onMaterialReady = () => {},
  },
  ref
) {
  // ───────────────────────────────────────────────────────────────────────────
  // Refs for geometry & material
  // ───────────────────────────────────────────────────────────────────────────
  const meshRef = useRef();            // Reference to the THREE.Points mesh
  const materialRef = useRef();        // Reference to the custom FireMaterial

  // Buffers for particle attributes
  const positions = useRef(new Float32Array(count * 3));
  const ages      = useRef(new Float32Array(count));
  const sizes     = useRef(new Float32Array(count));
  const velocities = useRef([]);       // Array of THREE.Vector3 velocities per particle

  // ───────────────────────────────────────────────────────────────────────────
  // Initialize particle data on mount or when count changes
  // ───────────────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    velocities.current = new Array(count);
    for (let i = 0; i < count; i++) {
      resetParticle(i);
    }
  }, [count]);

  // Expose the mesh to parent components via ref
  useImperativeHandle(ref, () => meshRef.current);

  // Notify parent when the material instance is ready
  useEffect(() => {
    if (materialRef.current) {
      onMaterialReady(materialRef.current);
    }
  }, [onMaterialReady]);

  // ───────────────────────────────────────────────────────────────────────────
  // Helper: Reset or initialize a single particle’s state
  // ───────────────────────────────────────────────────────────────────────────
  const resetParticle = (i) => {
    // Randomized upward spawn within a vertical range
    const minY = 0, maxY = 20;
    const y = Math.random() * (maxY - minY) + minY;
    // Lerp radius from base to top for funnel effect
    const baseRadius = 12, topRadius = 1;
    const t = (y - minY) / (maxY - minY);
    const radius = THREE.MathUtils.lerp(baseRadius, topRadius, t);
    const angle = Math.random() * Math.PI * 2;

    // Position in a circular area
    positions.current[i * 3 + 0] = position[0] + Math.cos(angle) * radius;
    positions.current[i * 3 + 1] = position[1] + y;
    positions.current[i * 3 + 2] = position[2] + Math.sin(angle) * radius;

    // Assign a random upward velocity
    velocities.current[i] = new THREE.Vector3(
      (Math.random() - 0.5) * 0.05,
      1.0 + Math.random() * 1.5,
      (Math.random() - 0.5) * 0.05
    );

    // Initialize age and size
    ages.current[i]  = Math.random() * 0.5;
    sizes.current[i] = 25 + Math.random() * 25;
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Animation Loop: Update positions, ages, and reset when expired
  // ───────────────────────────────────────────────────────────────────────────
  useFrame((_, delta) => {
    if (!meshRef.current || velocities.current.length < count) return;

    for (let i = 0; i < count; i++) {
      ages.current[i] += delta * 0.5;
      if (ages.current[i] >= 1) {
        resetParticle(i);
      } else {
        const idx = i * 3;
        const v = velocities.current[i];
        positions.current[idx + 0] += v.x * delta;
        positions.current[idx + 1] += v.y * delta;
        positions.current[idx + 2] += v.z * delta;
      }
    }

    // Mark buffer attributes for update on GPU
    const geom = meshRef.current.geometry;
    geom.attributes.position.needsUpdate = true;
    geom.attributes.age.needsUpdate      = true;
    geom.attributes.size.needsUpdate     = true;
  });

  // ───────────────────────────────────────────────────────────────────────────
  // Memoized BufferGeometry Setup (one-time)
  // ───────────────────────────────────────────────────────────────────────────
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions.current, 3));
    geo.setAttribute("age",      new THREE.BufferAttribute(ages.current, 1));
    geo.setAttribute("size",     new THREE.BufferAttribute(sizes.current, 1));
    return geo;
  }, [count]);

  // ───────────────────────────────────────────────────────────────────────────
  // Render: THREE.Points with custom FireMaterial
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <points ref={meshRef} geometry={geometry} position={position} visible={visible}>
      <fireMaterial ref={materialRef} transparent depthWrite={false} />
    </points>
  );
});

export default FireParticles;
